import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, Ban } from 'lucide-react'
import { toast } from 'sonner'

import { useCancelProject, useProjectCancellationPreview } from '@/api/hooks'
import type { components } from '@/api/schema'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { formatApiErrorMessage } from '@/lib/api-error'
import { formatMoney } from '@/lib/utils'
import { ComposeTextarea } from '@/components/compose-field'

type Preview = components['schemas']['ProjectCancellationPreview']
type OrderPlan = components['schemas']['ProjectCancellationOrderPlan']
type PurchaseOrderPlan = components['schemas']['ProjectCancellationPurchaseOrderPlan']

const PO_OUTCOME: Record<string, string> = {
  email: 'Email cancellation',
  cancel: 'Cancel, never sent',
  already_cancelled: 'Already cancelled',
}

function orderOutcome(order: OrderPlan) {
  if (order.already_cancelled) return 'Already cancelled'
  if (order.shopify_order_name) {
    const refund = Number(order.refund_amount ?? 0)
    return refund > 0 ? `Cancel in Shopify, refund ${formatMoney(refund)}` : 'Cancel in Shopify'
  }
  return 'Cancel, accounting follows up on credit'
}

function PlanRow({ link, outcome, warning }: {
  link: React.ReactNode
  outcome: string
  warning?: boolean
}) {
  return (
    <li className="py-2">
      <div className="truncate text-sm font-medium">{link}</div>
      <div
        className={`mt-0.5 flex items-start gap-1.5 text-sm ${
          warning ? 'text-orange-700 dark:text-orange-300' : 'text-muted-foreground'
        }`}
      >
        {warning && <AlertTriangle aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
        <span>{outcome}</span>
      </div>
    </li>
  )
}

function PreviewBody({ preview }: { preview: Preview }) {
  const orders = preview.orders ?? []
  const purchaseOrders = preview.purchase_orders ?? []
  return (
    <div className="space-y-4">
      {orders.length > 0 && (
        <section aria-labelledby="cancel-customer-heading">
          <h3 id="cancel-customer-heading" className="text-xs font-medium text-muted-foreground">
            Customer
          </h3>
          <ul className="divide-y">
            {orders.map((order) => (
              <PlanRow
                key={order.uuid}
                link={
                  <Link to="/orders/$orderId" params={{ orderId: order.uuid }} className="hover:underline">
                    {order.shopify_order_name ? `Shopify order ${order.shopify_order_name}` : order.name ?? 'Order'}
                  </Link>
                }
                outcome={orderOutcome(order)}
              />
            ))}
          </ul>
        </section>
      )}
      {purchaseOrders.length > 0 && (
        <section aria-labelledby="cancel-manufacturers-heading">
          <h3 id="cancel-manufacturers-heading" className="text-xs font-medium text-muted-foreground">
            Manufacturers
          </h3>
          <ul className="divide-y">
            {purchaseOrders.map((po: PurchaseOrderPlan) => (
              <PlanRow
                key={po.uuid}
                link={
                  <Link to="/purchase-orders/$poId" params={{ poId: po.uuid }} className="hover:underline">
                    {po.po_number} <span className="font-normal text-muted-foreground">{po.manufacturer_name}</span>
                  </Link>
                }
                outcome={po.plan === 'manual' ? `You cancel: ${po.manual_reason ?? 'needs a person'}` : PO_OUTCOME[po.plan] ?? po.plan}
                warning={po.plan === 'manual'}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/** Cancels the whole project: Shopify order and refund, our orders, and unshipped POs. */
export function CancelProjectButton({ projectId, projectName }: { projectId: string; projectName: string }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const preview = useProjectCancellationPreview(projectId, open)
  const cancelProject = useCancelProject(projectId)

  const close = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setReason('')
  }

  const submit = () => {
    cancelProject.mutate(reason.trim(), {
      onSuccess: () => {
        close(false)
        toast.success('Project cancelled')
      },
      onError: (error) => toast.error(formatApiErrorMessage('Could not cancel the project', error)),
    })
  }

  const data = preview.data
  const blocked = data && !data.can_cancel

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="w-full text-destructive hover:text-destructive"
        onClick={() => setOpen(true)}
      >
        <Ban className="h-3.5 w-3.5" /> Cancel project
      </Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="pr-6">Cancel {projectName}?</DialogTitle>
          </DialogHeader>
          {preview.isLoading ? (
            <div className="space-y-2" aria-busy="true">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : preview.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {formatApiErrorMessage('Could not load what cancelling would do', preview.error)}
            </p>
          ) : data ? (
            <div className="space-y-4">
              {blocked && (
                <div
                  role="alert"
                  className="flex gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-100"
                >
                  <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
                  <p>{data.blocked_reason}</p>
                </div>
              )}
              {!blocked && <PreviewBody preview={data} />}
              {!blocked && (
                <div className="space-y-2">
                  <Label htmlFor="cancel-project-reason">Reason</Label>
                  <ComposeTextarea
                    id="cancel-project-reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    maxLength={500}
                    rows={2}
                  />
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => close(false)}>
              {blocked ? 'Close' : 'Keep project'}
            </Button>
            {data && !blocked && (
              <LoadingButton
                variant="destructive"
                loading={cancelProject.isPending}
                loadingText="Cancelling..."
                disabled={!reason.trim()}
                onClick={submit}
              >
                Cancel project
              </LoadingButton>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
